import {attachArchivePurpose} from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {readQuerySafe} from 'graphql-client/ApolloWrappers';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {addToProcessOutputRowUpdaterCache} from 'processes/tables/ProcessOutputRowUpdater';
import APIUtil from 'util/APIUtil';

import {
  ProcessTaskOutputsQuery,
  ProcessTaskOutputsQueryVariables,
} from '../graphql/__generated/ProcessTaskOutputsQuery';
import {PROCESS_TASK_OUTPUTS_QUERY} from '../graphql/GetProcessTask';

type ProcessTaskWithOutputsGql = ExtractByTypename<ProcessTaskOutputsQuery['processTask'], 'ProcessTaskNode'>;

type ProcessTaskWithOutputsOutputs = ProcessTaskWithOutputsGql['outputs'][number];

type ProcessTaskWithOutputsStatus = ProcessTaskWithOutputsGql['status'];

interface UpdateCachedProcessTaskWithOutputs {
  status: ProcessTaskWithOutputsStatus;
  outputs: Array<ProcessTaskWithOutputsOutputs>;
}

export interface UpdateTaskWithOutputsParams {
  taskId: string;
  updatedStatusAndOutputs: Partial<UpdateCachedProcessTaskWithOutputs>;
  archivedOutputs?: Array<string>;
  unarchivedOutputs?: Array<string>;
  purpose?: string;
}

function getTaskWithModifiedOutputArchivePurpose({
  task,
  archivedOutputs,
  unarchivedOutputs,
  purpose,
}: {
  task: ProcessTaskWithOutputsGql;
  archivedOutputs: Array<string>;
  unarchivedOutputs: Array<string>;
  purpose?: string;
}): ProcessTaskWithOutputsGql {
  assert(
    [...archivedOutputs, ...unarchivedOutputs].every((id) => APIUtil.isProcessOutput(id)),
    'IDs passed in to change output archive status are not valid output IDs.',
    {archivedOutputs, unarchivedOutputs}
  );
  const archiveIdsSet = new Set(archivedOutputs);
  const unarchiveIdsSet = new Set(unarchivedOutputs);

  if (archiveIdsSet.size > 0) {
    assert(purpose != null, 'Expected to pass in purpose to archive outputs.');
  }

  assert(
    !archivedOutputs.some((id) => unarchiveIdsSet.has(id)),
    'Archive output changes must be completely disjoint.'
  );

  task.outputs = task.outputs.map((output) => {
    // If no purpose is passed in, do not archive the output.
    if (archiveIdsSet.has(output.id) && purpose != null) {
      return attachArchivePurpose(output, purpose);
    }
    if (unarchiveIdsSet.has(output.id) && isArchived(output)) {
      return {...output, archiveRecord: null};
    }
    return output;
  });
  return task;
}

export function updateProcessTaskDetailsOutputsQueryCache({
  taskId,
  updatedStatusAndOutputs,
  archivedOutputs,
  unarchivedOutputs,
  purpose,
}: UpdateTaskWithOutputsParams): void {
  const response = readQuerySafe<ProcessTaskOutputsQuery, ProcessTaskOutputsQueryVariables>(
    {query: PROCESS_TASK_OUTPUTS_QUERY},
    {variables: {taskId}}
  );
  if (response == null) {
    return;
  }

  if (isInaccessible(response.processTask)) {
    assert(false, `Cannot update inaccessible response, id: ${response.processTask.invalidId}`);
  }

  const taskToWrite: ProcessTaskWithOutputsGql = {
    ...response.processTask,
    ...updatedStatusAndOutputs,
  };

  const taskWithModifiedOutputArchivePurpose = getTaskWithModifiedOutputArchivePurpose({
    task: taskToWrite,
    archivedOutputs: archivedOutputs ?? [],
    unarchivedOutputs: unarchivedOutputs ?? [],
    purpose,
  });

  getApolloClient().writeQuery({
    query: PROCESS_TASK_OUTPUTS_QUERY,
    data: {
      processTask: taskWithModifiedOutputArchivePurpose,
    },
    variables: {taskId},
  });
  if (updatedStatusAndOutputs.outputs != null) {
    addToProcessOutputRowUpdaterCache(updatedStatusAndOutputs.outputs);
  }
}
